import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionLabel } from "@/components/Reveal";

const CHAPTERS = [
  {
    n: "01",
    t: "A unique combination",
    d: "Not glutathione alone — Relumia pairs reduced L-Glutathione + Optiac™ with Vitamin C, Vitamin D3, Zinc Sulphate and Curcumin. Each active amplifies the next for brightening you can actually see.",
  },
  {
    n: "02",
    t: "Cost-effective luxury",
    d: "Clinic-grade actives shouldn't demand clinic prices. Relumia delivers a premium, dermatologist-formulated regimen at a price that respects everyday budgets — luxury without the markup.",
  },
  {
    n: "03",
    t: "A celebrated brand",
    d: "Trusted for its results and its transparency. Relumia is built on honest labels, consistent quality and a formula people return to — the mark of a glutathione brand that earns its reputation.",
  },
  {
    n: "04",
    t: "Glow from within",
    d: "Real radiance isn't painted on. Relumia supports brighter, even-toned, youthful skin from the inside — one tablet, one daily ritual, a visibly luminous result.",
  },
];

function Chapter({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const numY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const flip = i % 2 === 1;

  return (
    <div
      ref={ref}
      className="relative grid lg:grid-cols-2 gap-6 items-center py-16 md:py-24 border-t border-champagne"
    >
      <motion.span
        style={{ y: numY }}
        className={`pointer-events-none select-none absolute -z-0 font-serif font-light leading-none text-[24vw] lg:text-[16vw] text-[color:var(--relumia-champagne)] opacity-60 top-1/2 -translate-y-1/2 ${
          flip ? "right-0 lg:right-10" : "left-0 lg:left-10"
        }`}
      >
        {c.n}
      </motion.span>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={`relative z-10 ${flip ? "lg:order-2 lg:pl-10" : "lg:pr-10"}`}
      >
        <div className="font-serif text-gold text-2xl mb-4">Chapter {c.n}</div>
        <h3 className="font-serif font-light tracking-tight leading-[1.03] text-4xl md:text-5xl lg:text-6xl text-[color:var(--relumia-ink)]">
          {c.t}
        </h3>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
        className={`relative z-10 ${flip ? "lg:order-1" : ""}`}
      >
        <p className="text-base leading-relaxed text-[color:var(--relumia-muted)] max-w-md">
          {c.d}
        </p>
      </motion.div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative z-10 bg-surface py-24 md:py-28 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-3xl mb-4">
          <SectionLabel>Why Relumia</SectionLabel>
          <h2 className="font-serif font-light tracking-tight leading-[1.03] text-4xl md:text-6xl text-[color:var(--relumia-ink)]">
            Four reasons it became a{" "}
            <span className="italic text-gold">favourite</span>.
          </h2>
        </div>
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.n} c={c} i={i} />
        ))}
      </div>
    </section>
  );
}
